import * as THREE from 'three';

// Procedural circular shadow texture (64x64, soft falloff)
let shadowTexture: THREE.CanvasTexture | null = null;
const activeBlobShadows = new Set<THREE.Mesh>();

function getOrCreateShadowTexture(): THREE.CanvasTexture {
  if (shadowTexture) return shadowTexture;

  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  // Radial gradient: dark center -> transparent edge
  const gradient = ctx.createRadialGradient(
    size / 2, size / 2, 0,
    size / 2, size / 2, size / 2,
  );
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0.45)');
  gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.25)');
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);

  shadowTexture = new THREE.CanvasTexture(canvas);
  shadowTexture.needsUpdate = true;
  return shadowTexture;
}

/** Create a blob shadow mesh to attach under a player group. */
export function createBlobShadow(): THREE.Mesh {
  const texture = getOrCreateShadowTexture();

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    opacity: 0.35,
    depthWrite: false,
    blending: THREE.MultiplyBlending,
  });

  const geometry = new THREE.PlaneGeometry(1.4, 1.4);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = 0.02; // just above ground to avoid z-fighting

  // Don't participate in raycasting or shadows
  mesh.raycast = () => {};
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  mesh.name = '__blob_shadow__';
  activeBlobShadows.add(mesh);

  return mesh;
}

/** Update blob shadow Y position and opacity based on distance to ground.
 *  Call this each frame for each player that has a blob shadow. */
export function updateBlobShadow(shadow: THREE.Mesh, distToGround: number): void {
  // Fade out as player gets further from ground (jumping/falling)
  const maxDist = 3.0;
  const opacity = Math.max(0, 1 - distToGround / maxDist) * 0.35;
  (shadow.material as THREE.MeshBasicMaterial).opacity = opacity;

  // Position shadow at ground level (relative to parent group)
  shadow.position.y = -distToGround + 0.02;
}

export function releaseBlobShadow(shadow: THREE.Mesh | null): void {
  if (!shadow) return;
  activeBlobShadows.delete(shadow);
  shadow.geometry.dispose();
  (shadow.material as THREE.MeshBasicMaterial).dispose();
}

/** Dispose shared shadow texture. Call on cleanup. */
export function disposeBlobShadows(): void {
  for (const shadow of activeBlobShadows) {
    shadow.geometry.dispose();
    (shadow.material as THREE.MeshBasicMaterial).dispose();
  }
  activeBlobShadows.clear();
  shadowTexture?.dispose();
  shadowTexture = null;
}
